/*
 *   File :multi-line.js
 *   Author : https://evoqins.com
 *   Description : multi line chart component
 *   Integrations : chart.js
 *   Version : 1.0.0
*/

import { memo } from "react";
import React, { useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

// Styles - Colors
import Color from "../../Styles/color.scss";

// Assets
import LegendIcon1 from "../../Assets/Images/Basket/chart-legend-icon-portfolio.svg";
import LegendIcon2 from "../../Assets/Images/Basket/chart-legend-icon-index.svg";

const img = new Image();
img.src = "../../Assets/Images/Basket/chart-legend-icon-index.svg";

// Register the components
ChartJS.register(LineElement, CategoryScale, LinearScale, Tooltip, Legend, PointElement);

// Setting position of tooltip
Tooltip.positioners.right = (elements) => {

  if (elements.length != 0) {
    return {
      x: elements[0].element.x,
      y: elements[0].element.y - 75,
    }
  }
  return {
    x: 0,
    y: 0,
  }

};


const MultiLineChart = (props) => {

  const chartRef = useRef(null);
  const chartTooltipRef = useRef(null);

  // Custom plugin to draw a line on mouse move
  const mouseLinePlugin = {
    id: 'mouseLine',
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx;
      if (chart.tooltip._active && chart.tooltip._active.length) {
        const activePoints = chart.tooltip._active;
        chartTooltipRef.current = activePoints
        const x = activePoints[0].element.x;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;

        // Gradient color for tooltip line
        const gradient = ctx.createLinearGradient(0, 0, 0, chart.height);
        gradient.addColorStop(0, '#0D60D3'); // Starting color
        gradient.addColorStop(1, 'rgba(13, 96, 211, 0)'); // Ending color

        // Draw vertical line
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, bottomY);
        ctx.lineTo(x, topY);
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = gradient; // Color for vertical tooltip line
        ctx.stroke();
        ctx.restore();

        activePoints.forEach((point, index) => {
          const { element } = point;
          const pointX = element.x;
          const pointY = element.y;

          // Draw the pointer (circle) at the active point
          ctx.save();
          ctx.beginPath();
          ctx.arc(pointX, pointY, 4, 0, 2 * Math.PI); // Pointer size
          ctx.fillStyle = index === 0 ? props.borderColor1 : props.borderColor2; // Different color for each dataset
          ctx.fill();
          ctx.restore();
        });
      }
    },
    // beforeRender: (chart) => {
    //   // Get the first dataset
    //   const { tooltip, data } = chart;

    //   // If there's no data, exit
    //   if (!data.datasets.length) {
    //     return;
    //   }

    //   // Show tooltip at the first point if not visible
    //   if (!tooltip._active || !tooltip._active.length) {
    //     setTimeout(() => {
    //       if (chartTooltipRef.current) {
    //         chart.tooltip.setActiveElements(
    //           chartTooltipRef.current
    //         );
    //         chart.update(); // Update the chart to show the tooltip
    //       }
    //     }, 0);
    //   }
    // },
    afterRender: (chart) => {
      // Get the first dataset
      const { tooltip, data } = chart;

      // If there's no data, exit
      if (!data.datasets.length) {
        return;
      }

      // Show tooltip at the first point if not visible
      if ((!tooltip._active || !tooltip._active.length) && chartTooltipRef.current) {

        chart.tooltip.setActiveElements(
          chartTooltipRef.current
        );
        chart.update(); // Update the chart to show the tooltip

      }
    },
  };

  const shadowTooltip = {
    id: 'shadowedTooltip',
    afterDraw(chart) {
      const tooltip = chart.tooltip;
      if (tooltip && tooltip.opacity) {
        const ctx = chart.ctx;

        // Set shadow properties for tooltip
        ctx.shadowColor = 'rgba(212, 212, 212, 1)';
        ctx.shadowBlur = 15;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 17;

        // Save and restore context to apply shadow only to the tooltip
        ctx.save();
        tooltip.draw(ctx);
        ctx.restore();

        // Reset shadow properties for other elements
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
    }
  }

  useEffect(() => {
    const chart = chartRef.current;
    if (chart) {
      // Set the initial position of the line to the last item in the dataset
      const lastElementIndex = props.labels.length - 1;
      const lastElement = chart.scales.x.getPixelForValue(lastElementIndex);
      chart.options.plugins.mouseLine.x = lastElement;
      chart.options.plugins.mouseLine.initialIndex = lastElementIndex;
      chart.options.plugins.mouseLine.color = `${Color.anti_flash_white}`; // Ensure this color is used

      // Trigger tooltip for the last element initially
      if (props.dataset2.length === 0) {
        chart.tooltip.setActiveElements([
          {
            datasetIndex: 0, // assuming first dataset, change if needed
            index: lastElementIndex,
          },
        ]);
      } else {
        chart.tooltip.setActiveElements([
          {
            datasetIndex: 0, // assuming first dataset, change if needed
            index: lastElementIndex,
          },
          {
            datasetIndex: 1, // assuming second dataset, change if needed
            index: lastElementIndex,
          }
        ]);
      }
    }
  }, [props.labels, props.dataset2]);

  // Datasets
  const data = {
    labels: props.labels,
    datasets: [
      {
        label: 'Portfolio value : ',
        data: props.dataset1,
        borderColor: props.borderColor1,
        pointBackgroundColor: props.borderColor1,
        pointRadius: 0,
        backgroundColor: 'transparent',
        borderWidth: 1.8,
        fill: true,
        lineTension: 0.5,
      },
      {
        label: 'Index value : ',
        data: props.dataset2 || [],
        borderColor: props.borderColor2,
        pointBackgroundColor: props.borderColor2,
        pointRadius: 0,
        backgroundColor: 'transparent',
        borderWidth: 1.8,
        fill: true,
        lineTension: 0.5,
      },
    ].filter(Boolean),
  };

  // Customization
  const options = {
    layout: {
      padding: {
        // top: 20,
        // bottom: 20,
        // left: 20,
        right: 15
      }
    },
    plugins: {
      tooltip: {
        position: "right", // for tooltip position
        enabled: true,
        backgroundColor: Color.white,
        bodyColor: Color.black,
        bodySpacing: 8,
        bodyFont: {
          family: "Lato",
          weight: 500,
          size: 12,
        },
        boxWidth: 8,
        footerFont: {
          family: "Lato",
          weight: "400",
          size: 10,
        },
        footerColor: Color.slate_gray,
        callbacks: {
          title: () => '',
          label: (context) => {
            const value = context.raw;
            return `${context.dataset.label} ₹${value}`;
          },
          footer: (context) => {
            const dateLabel = context[0].label;
            return `As on ${dateLabel}`;
          },
        },
        usePointStyle: true,
        padding: 8,
        displayColor: true,
        boxPadding: 8, // padding between icon and label
        cornerRadius: 8,
        caretSize: 0,
        caretPadding: 10,
        xAlign: "center",
        // yAlign : "center"
      },
      legend: {
        display: false,
      },
      mouseLine: {
        x: NaN,
        color: 'rgba(0, 0, 0, 0.5)',
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    scales: {
      x: {
        beginAtZero: true,
        offset: false,
        border: {
          display: false,
        },
        ticks: {
          font: {
            family: "Lato",
            weight: 400,
            size: 11,
          },
          color: Color.dark_charcoal,
          textAlign: 'center',
          maxRotation: 0,
          minRotation: 0,
          callback: function (value, index, values) {
            const totalLabels = props.labels.length;
            const step = Math.floor(totalLabels / 10);

            // Show the first, last, and labels at calculated intervals
            if (index === 0 || index % step === 0) {
              const date_parts = this.getLabelForValue(value).split(' ');
              let year = date_parts[2].split("").splice(2, 2).join("");
              return `${date_parts[0]} ${year}`
            }
            return ''; // Hide other labels
          },
          autoSkip: false,
        },
        grid: {
          display: false
        },

      },
      y: {
        position: 'right',
        min: Math.round(Math.min(...props.dataset1, ...props.dataset2)).toFixed(0),
        max: (Math.round(Math.max(...props.dataset1, ...props.dataset2)) * 1.1).toFixed(0),
        beginAtZero: true,
        offset: false,
        ticks: {
          font: {
            family: "Lato",
            weight: 400,
            size: 12,
          },
          color: Color.dark_charcoal,
          // maxTicksLimit: 6,
          // stepSize: 4,
          callback: function (value, index, values) {
            const step = Math.ceil(values.length / 6);
            if (index % step === 0) {
              return index === 0 ? '' : "₹" + value;
            }
            return '';
          },
          autoSkip: false
        },
        border: {
          display: false,
        },
        grid: {
          color: Color.anti_flash_white,
          lineWidth: 0.5
        },
      },
    },
  };

  return (
    <div className='position-relative cursor-pointer'>

      <Line ref={chartRef} data={data} options={options}
        plugins={[mouseLinePlugin, shadowTooltip]}
        className='position-relative '
      />

      {/* Custom Legend */}
      <div className="d-flex justify-content-center gap-10px">
        <div>
          <img src={LegendIcon1}
            alt="portfolio-icon"
            draggable="false"
            className="img-fluid" />
          <span className="e-text-philippine-gray e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px">
            Portfolio value
          </span>
        </div>
        <div>
          <img src={LegendIcon2}
            alt="index icon"
            draggable="false"
            className="img-fluid" />
          <span className="e-text-philippine-gray e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px">
            {props.legend2Label}
          </span>
        </div>
      </div>
    </div>
  );
};

export default memo(MultiLineChart);
